import { Injectable, Signal, computed, effect, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { ControlService } from '../../library/control.service';
import { RealtimeService } from '../../library/realtime/realtime.service';
import { CallStoreService } from '../../library/call-store/call-store.service';
import { CallService } from 'src/app/services/learner/call.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';
import { AppConstants } from 'src/app/app-constants';
import { IInitiatePathwayStepSimulationResponse } from 'src/app/interfaces/call/iinitiate-pathway-step-simulation';
import { ICallRealtime } from 'src/app/interfaces/icall-realtime';
import { IPathwayRunStepResponse } from 'src/app/interfaces/ipathway-enrollment';
import { AssignmentService } from 'src/app/services/learner/assignment.service';
import { ICallDetailModal } from 'src/app/interfaces/icall-detail-modal';
import { PathwayStepType, RoleplayFooterState } from 'src/app/utils/enums/learner/pathway.enum';
import { PlayerService } from '../../library/player.service';
import { PathwaysService } from 'src/app/services/learner/pathways.service';

@Injectable()
export class PathwayRoleplayService {
  mainIsLoading = signal(false);
  isPreamble = signal(false);
  callDetails = signal<ICallDetailModal | null>(null);
  readonly footerState = signal<RoleplayFooterState>(RoleplayFooterState.Preamble);
  readonly canProceedFromPreamble = signal<boolean>(false);
  readonly isPrimaryActionDisabled = computed(() => {
    if (this.mainIsLoading()) return true;
    const state = this.footerState();
    if (state === RoleplayFooterState.Preamble) return !this.canProceedFromPreamble();
    // For active state we can respect end-call disable from store (if present)
    if (state === RoleplayFooterState.Active)
      return this.lastCallStore?.getDisableEndCallButton?.() ?? false;
    return false;
  });
  readonly captionText = signal<string>('');
  private subscriptions: Subscription[] = [];
  private enrollmentUuid: string = '';
  private lastRunStep: IPathwayRunStepResponse | null = null;
  private lastModuleAssignmentUuid: string = '';
  private lastRealtimeService: RealtimeService | undefined;
  private lastControlService: ControlService | undefined;
  private lastCallStore: CallStoreService | undefined;
  private preambleAutoCompleted = false;

  setFooterState(state: RoleplayFooterState): void {
    this.footerState.set(state);
  }

  constructor(
    private readonly callService: CallService,
    private readonly loggerService: LoggerService,
    private readonly assignmentService: AssignmentService,
    private readonly playerService: PlayerService,
    private readonly pathwaysService: PathwaysService,
  ) {
    effect(() => {
      const speech = this.playerService.avatarSpeech();
      if (speech) this.captionText.set(speech);
    });
  }

  init(params: {
    moduleAssignmentUuid: string;
    enrollmentStepUuid: string;
    enrollmentUuid: string;
    stepType: string;
    runStepResponse: Signal<IPathwayRunStepResponse | null> | undefined;
    realtimeService: RealtimeService | undefined;
    controlService: ControlService | undefined;
    callStore: CallStoreService | undefined;
  }): void {
    const runStep = params.runStepResponse?.();
    this.lastRunStep = runStep ?? null;
    this.enrollmentUuid = params.enrollmentUuid ?? '';
    this.lastModuleAssignmentUuid = params.moduleAssignmentUuid ?? '';
    this.lastRealtimeService = params.realtimeService;
    this.lastControlService = params.controlService;
    this.lastCallStore = params.callStore;

    const isPreamble = runStep?.isPreamble ?? false;
    const enrollmentStepUuid =
      runStep?.stepUuid ?? params.enrollmentStepUuid ?? '';

    if (isPreamble) {
      this.isPreamble.set(true);
      this.footerState.set(RoleplayFooterState.Preamble);
      this.canProceedFromPreamble.set(false);
      this.preambleAutoCompleted = false;

      // Roleplay preamble has no user video: ensure camera is off.
      if (params.controlService?.isVideoOn) {
        params.controlService.toggleVideo();
      }
    } else {
      this.footerState.set(RoleplayFooterState.Active);
    }

    if (isPreamble && params.stepType === PathwayStepType.PRACTICE_CALL) {
      const callUuid = runStep?.callUuid ?? '';
      this.startPreambleInteraction({
        ...params,
        callUuid,
        enrollmentStepUuid: enrollmentStepUuid,
      });
    }

    if (params.stepType === 'PRACTICE_CALL' && !isPreamble) {
      const callUuid = runStep?.callUuid ?? '';
      const callType = runStep?.callType ?? '';

      this.initiateAndStartCall({
        ...params,
        callUuid,
        callType,
        enrollmentStepUuid: enrollmentStepUuid,
      });
    }

    this.fetchCallDetails({
      stepType: params.stepType,
      callUuid: runStep?.callUuid ?? '',
      moduleAssignmentUuid: params.moduleAssignmentUuid,
    });
  }

  launchSimulationFromPreamble(): void {
    if (!this.enrollmentUuid) {
      this.loggerService.error('[ROLEPLAY] launchSimulationFromPreamble: missing enrollmentUuid');
      return;
    }
    if (!this.lastRunStep?.stepUuid) {
      this.loggerService.error('[ROLEPLAY] launchSimulationFromPreamble: missing stepUuid');
      return;
    }
    if (!this.lastModuleAssignmentUuid) {
      this.loggerService.error('[ROLEPLAY] launchSimulationFromPreamble: missing moduleAssignmentUuid');
      return;
    }
    if (!this.lastRealtimeService || !this.lastControlService || !this.lastCallStore) {
      this.loggerService.error('[ROLEPLAY] launchSimulationFromPreamble: missing required services');
      return;
    }

    this.mainIsLoading.set(true);

    try {
      this.lastRealtimeService.endRealtimeConversation(true, true);
    } catch (err) {
      this.loggerService.error('[ROLEPLAY] launchSimulationFromPreamble: endRealtimeConversation failed', err);
    }

    const runMainStep = () => {
      this.subscriptions.push(
        this.pathwaysService
          .runStepByEnrollmentUuid(this.enrollmentUuid, this.lastRunStep!.stepUuid!)
          .subscribe({
            next: (runResponse: IPathwayRunStepResponse) => {
              this.lastRunStep = runResponse;

              if (!runResponse.callUuid) {
                this.loggerService.error('[ROLEPLAY] launchSimulationFromPreamble: runStep returned missing callUuid');
                this.mainIsLoading.set(false);
                return;
              }

              this.isPreamble.set(false);
              this.footerState.set(RoleplayFooterState.Active);

              this.initiateAndStartCall({
                moduleAssignmentUuid: this.lastModuleAssignmentUuid,
                callUuid: runResponse.callUuid,
                callType: runResponse.callType ?? '',
                enrollmentStepUuid: runResponse.stepUuid ?? '',
                realtimeService: this.lastRealtimeService,
                controlService: this.lastControlService,
                callStore: this.lastCallStore,
              });
              this.mainIsLoading.set(false);
            },
            error: (err) => {
              this.loggerService.error('[ROLEPLAY] launchSimulationFromPreamble: runStep failed', err);
              this.mainIsLoading.set(false);
            },
          }),
      );
    };

    if (this.preambleAutoCompleted) {
      runMainStep();
      return;
    }

    this.subscriptions.push(
      this.pathwaysService.completeStepByEnrollmentUuid(this.enrollmentUuid).subscribe({
        next: () => runMainStep(),
        error: (err) => {
          this.loggerService.error('[ROLEPLAY] launchSimulationFromPreamble: completeStep failed', err);
          this.mainIsLoading.set(false);
        },
      }),
    );
  }

  fetchCallDetails(params: {
    stepType: string;
    callUuid: string;
    moduleAssignmentUuid: string;
  }): void {
    if (this.callDetails()) {
      return;
    }

    if (params.stepType !== PathwayStepType.PRACTICE_CALL) {
      return;
    }

    if (!params.callUuid || !params.moduleAssignmentUuid) {
      this.loggerService.error('[ROLEPLAY] fetchCallDetails: missing callUuid or moduleAssignmentUuid');
      return;
    }

    this.subscriptions.push(
      this.assignmentService
        .getCallDetails(params.callUuid, params.moduleAssignmentUuid)
        .subscribe({
          next: (response: ICallDetailModal) => {
            this.callDetails.set(response);
          },
          error: (error) => {
            this.loggerService.error('[ROLEPLAY] fetchCallDetails: failed to load call details', error);
          },
        })
    );
  }

  private initiateAndStartCall(params: {
    moduleAssignmentUuid: string;
    callUuid: string;
    callType: string;
    enrollmentStepUuid: string;
    realtimeService: RealtimeService | undefined;
    controlService: ControlService | undefined;
    callStore: CallStoreService | undefined;
  }): void {
    const { moduleAssignmentUuid, callUuid, callType, enrollmentStepUuid } = params;

    if (!moduleAssignmentUuid || !callUuid || !enrollmentStepUuid) {
      this.loggerService.error(
        '[ROLEPLAY] Missing moduleAssignmentUuid, callUuid or enrollmentStepUuid',
      );
      return;
    }

    this.mainIsLoading.set(true);

    this.subscriptions.push(
      this.callService
        .initiatePathwayStepSimulation(
          moduleAssignmentUuid,
          callUuid,
          PathwayConstants.PRACTICE_TYPE_PATHWAY,
          callType,
          enrollmentStepUuid,
        )
        .subscribe({
          next: async (res: IInitiatePathwayStepSimulationResponse) => {
            try {
              if (!params.realtimeService) {
                this.loggerService.error('[ROLEPLAY] Missing realtimeService');
                this.mainIsLoading.set(false);
                return;
              }

              const conversationId = res.conversationId ?? '';
              const conversationUrl = res.conversationUrl ?? '';

              if (!conversationId || !conversationUrl) {
                this.loggerService.error(
                  '[ROLEPLAY] initiatePathwayStepSimulation response missing conversation details',
                );
                this.mainIsLoading.set(false);
                return;
              }

              if (params.controlService && params.callStore) {
                params.controlService.setQueryParams(
                  moduleAssignmentUuid,
                  callUuid,
                  PathwayConstants.PRACTICE_TYPE_PATHWAY,
                  callType,
                  undefined,
                );
                params.callStore.setCallUuid(callUuid);
                params.callStore.setCallType(callType);
                params.callStore.setPracticeCallUuid(res.practiceCallUuid);
                params.callStore.setCurrentView(AppConstants.VIEW_PRACTICE);
                params.callStore.setDisplayPostCall(false);
                params.callStore.setIsMicOn(
                  params.callStore.getUserMicPreference(),
                );
              }

              const realtimePayload: ICallRealtime = {
                practiceCallUuid: res.practiceCallUuid,
                conversationId,
                conversationUrl,
                callName: res.callName ?? '',
                avatarName: res.avatarName ?? '',
                pronounceWords: res.pronounceWords ?? [],
                resumeMessage: res.resumeMessage ?? [],
                visualAid: res.visualAid ?? undefined,
                customBackgroundUrl: res.customBackgroundUrl ?? undefined,
                hideResultsForLearners: res.hideResultsForLearners ?? null,
              };

              // false = conversational, not guided
              await params.realtimeService.initializeFromStartConversationResponse(
                realtimePayload,
                false,
              );

              params.realtimeService.conversationInitializedByPathwayFlow = true;

              params.callStore?.setCurrentView(AppConstants.VIEW_PRACTICE);
                                          
              this.mainIsLoading.set(false);
              
            } catch (error) {
              this.loggerService.error(
                '[ROLEPLAY] initiateAndStartCall failed',
                error,
              );
              this.mainIsLoading.set(false);
            }
          },
          error: (err) => {
            this.loggerService.error(
              '[ROLEPLAY] initiatePathwayStepSimulation error',
              err,
            );
            this.mainIsLoading.set(false);
          },
        }),
    );
  }

  private startTavusCall(params: {
    callUuid: string;
    callType: string;
    moduleAssignmentUuid: string;
    realtimeService: RealtimeService | undefined;
    controlService: ControlService | undefined;
  }): void {
    if (!params.realtimeService) {
      this.loggerService.error('[ROLEPLAY] Missing realtimeService');
      return;
    }

    if (!params.callUuid) {
      this.loggerService.error('[ROLEPLAY] Missing callUuid from API response');
      return;
    }

    if (!params.callType) {
      this.loggerService.error('[ROLEPLAY] Missing callType from API response');
      return;
    }

    if (!params.moduleAssignmentUuid) {
      this.loggerService.error('[ROLEPLAY] Missing moduleAssignmentUuid');
      return;
    }

    if (!params.controlService) {
      this.loggerService.error('[ROLEPLAY] Missing controlService');
      return;
    }

    try {
      params.realtimeService.startCallRequest(false).catch((error) => {
        this.loggerService.error('[ROLEPLAY] startCallRequest failed', error);
      });
    } catch (error) {
      this.loggerService.error('[ROLEPLAY] startTavusCall failed', error);
    }
  }

  private startPreambleInteraction(params: {
    moduleAssignmentUuid: string;
    callUuid: string;
    enrollmentStepUuid: string;
    realtimeService: RealtimeService | undefined;
    controlService: ControlService | undefined;
    callStore: CallStoreService | undefined;
  }): void {
    if (!params.realtimeService || !params.controlService || !params.callStore) {
      this.loggerService.error('[ROLEPLAY] startPreambleInteraction: missing required services');
      return;
    }

    if (!params.callUuid || !params.moduleAssignmentUuid || !params.enrollmentStepUuid) {
      this.loggerService.error('[ROLEPLAY] startPreambleInteraction: missing callUuid, moduleAssignmentUuid or enrollmentStepUuid');
      return;
    }

    params.controlService.setQueryParams(
      params.moduleAssignmentUuid,
      params.callUuid,
      PathwayConstants.PRACTICE_TYPE_PATHWAY,
      AppConstants.CALL_TYPE_FRAMING,
      undefined,
    );
    params.callStore.setCallUuid(params.callUuid);
    params.callStore.setCallType(AppConstants.CALL_TYPE_FRAMING);

    params.realtimeService
      .startFramingCallRequest(params.enrollmentStepUuid)
      .then(() => {})
      .catch((error) => {
        this.loggerService.error('[ROLEPLAY] startPreambleInteraction: framing call failed', error);
      });

    this.subscriptions.push(
      params.realtimeService.welcomeMessageSpoken$.subscribe(() => {
        if (!this.isPreamble() || this.preambleAutoCompleted) return;
        this.preambleAutoCompleted = true;
        this.canProceedFromPreamble.set(true);
        this.pathwaysService.completeStepByEnrollmentUuid(this.enrollmentUuid).subscribe({
          error: (err) => {
            this.loggerService.error('[ROLEPLAY] preamble completeStepOnly failed', err);
          },
        });
      }),
    );
  }

  endCall(): void {
    console.log("End call called");
  }

  destroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
  }
}
